import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { API } from 'src/common/config/API';
import { paths, userRoles } from 'src/common/config/TextMessage';
import { setOpenSnackBar } from 'src/redux/siteAuditReducer';

function readUpgradeSessionFlag(): boolean {
  try {
    return localStorage.getItem('hideUpgradePricingModal') === 'true';
  } catch {
    return false;
  }
}

/**
 * Manages the "Stay on Current Plan" pricing-version modal:
 * - Determines whether to show the modal based on user and plan state
 * - Handles the "Stay on current plan" API call
 * - Handles the "Upgrade" navigation action
 *
 * @returns State and handlers for `UpgradeNewPlanModal`.
 */
export const usePricingModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isPricingModalLoading, setIsPricingModalLoading] = useState(false);
  const [isUpgradeClicked, setIsUpgradeClicked] = useState(false);

  const userDetails: any = useSelector((state: RootState) => state.counter.userDetails);
  const isLoadingUserDetails = useSelector((state: RootState) => state.counter.isLoadingUserDetails);
  const activePlanName = useSelector((state: RootState) => state.user.activePlan);
  const planDetails = useSelector((state: RootState) => state.user.planDetails);

  const isMember = userDetails?.role_type === userRoles?.member;
  const isBillingPage = pathname === paths.billing || pathname === paths.googleBusiness;
  const isUpgradeSessionClicked = readUpgradeSessionFlag();

  const shouldShowModal =
    !isMember &&
    !isUpgradeClicked &&
    !isUpgradeSessionClicked &&
    !isBillingPage &&
    !isLoadingUserDetails &&
    Boolean(userDetails?.email) &&
    (userDetails?.pricingVersion === undefined || userDetails?.pricingVersion === null);

  useEffect(() => {
    if (shouldShowModal) setShowPricingModal(true);
  }, [shouldShowModal]);

  const handleStayOnCurrentPlan = () => {
    setIsPricingModalLoading(true);
    API.updatePricingVersion(
      API.EndPoint.updatePricingVersion,
      API.Method.patch,
      undefined,
      { pricingVersion: 'v1' },
      (response: any) => {
        setIsPricingModalLoading(false);
        if (response?.data?.status === 'success') {
          setShowPricingModal(false);
          setIsUpgradeClicked(true);
        } else {
          dispatch(
            setOpenSnackBar({
              message:
                response?.data?.message || 'Something went wrong. Please try again later.',
              type: 'error'
            })
          );
        }
      }
    );
  };

  const handleUpgrade = () => {
    try {
      localStorage.setItem('hideUpgradePricingModal', 'true');
    } catch {}
    setIsUpgradeClicked(true);
    setShowPricingModal(false);
    navigate(paths.billing);
  };

  return {
    showPricingModal,
    isPricingModalLoading,
    activePlanName,
    planDetails,
    handleStayOnCurrentPlan,
    handleUpgrade
  };
};
